import { useState, useCallback } from "react";
import { useFeedback } from '@/hooks/useFeedback';
import { TYPOGRAPHY, RESPONSIVE, TABS } from '@/design-system';
import { LComponents } from '@/components/i18n';
import { Button } from "@/components/ui/button";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { incrementFAQFeedback } from "@/hooks/useFAQs";
import { cn } from "@/lib/utils";

interface FAQFeedbackButtonsProps {
  faqId: string;
  helpfulCount: number;
  notHelpfulCount: number;
  onFeedback?: (helpful: boolean) => void;
}

const STORAGE_KEY = 'faq_feedback';

const getFeedbackState = (faqId: string): 'helpful' | 'not_helpful' | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const feedback = JSON.parse(stored);
    return feedback[faqId] || null;
  } catch {
    return null;
  }
};

const setFeedbackState = (faqId: string, value: 'helpful' | 'not_helpful') => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const feedback = stored ? JSON.parse(stored) : {};
    feedback[faqId] = value;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(feedback));
  } catch {
    // Silently fail
  }
};

export const FAQFeedbackButtons = ({
  faqId,
  helpfulCount,
  notHelpfulCount,
  onFeedback,
}: FAQFeedbackButtonsProps) => {
  const { notify } = useFeedback();

  const [userFeedback, setUserFeedback] = useState<'helpful' | 'not_helpful' | null>(
    () => getFeedbackState(faqId)
  );
  const [counts, setCounts] = useState({ helpful: helpfulCount, notHelpful: notHelpfulCount });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFeedback = useCallback(async (isHelpful: boolean) => {
    if (userFeedback || isSubmitting) return;

    setIsSubmitting(true);
    const feedbackType = isHelpful ? 'helpful' : 'not_helpful';

    // Optimistic update
    setCounts(prev => ({
      helpful: isHelpful ? prev.helpful + 1 : prev.helpful,
      notHelpful: !isHelpful ? prev.notHelpful + 1 : prev.notHelpful,
    }));
    setUserFeedback(feedbackType);
    setFeedbackState(faqId, feedbackType);

    try {
      await incrementFAQFeedback(faqId, isHelpful);
      notify.success('thanks_for_your_feedback', "Thanks for your feedback!");
      onFeedback?.(isHelpful);
    } catch (error) {
      // Revert optimistic update
      setCounts(prev => ({
        helpful: isHelpful ? prev.helpful - 1 : prev.helpful,
        notHelpful: !isHelpful ? prev.notHelpful - 1 : prev.notHelpful,
      }));
      setUserFeedback(null);
      notify.error('failed_to_submit_feedback', "Failed to submit feedback");
    } finally {
      setIsSubmitting(false);
    }
  }, [faqId, userFeedback, isSubmitting, onFeedback]);

  const hasVoted = userFeedback !== null;

  return (
    <div className={cn(
      RESPONSIVE.flexRowCenterGap2,
      TABS.contentNested,
      'pt-3 border-t border-border/50'
    )}>
      <span className={cn(TYPOGRAPHY.caption, 'mr-2')}><LComponents id="faq_feedback.was_this_helpful" defaultValue="Was this helpful?" /></span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleFeedback(true)}
        disabled={hasVoted || isSubmitting}
        className={cn(
          "h-8 px-2 gap-1.5",
          userFeedback === 'helpful' && "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
        )}
      >
        <ThumbsUp className="h-3.5 w-3.5" />
        <span className="text-xs">{counts.helpful}</span>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleFeedback(false)}
        disabled={hasVoted || isSubmitting}
        className={cn(
          "h-8 px-2 gap-1.5",
          userFeedback === 'not_helpful' && "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
        )}
      >
        <ThumbsDown className="h-3.5 w-3.5" />
        <span className="text-xs">{counts.notHelpful}</span>
      </Button>
    </div>
  );
};
